import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import {
  getCashMovementsReport,
  getConsumptionBySectorReport,
  getDelinquencyReport,
  getExecutiveQuarterlyReport,
  getInventoryValuationReport,
  getLegalQuarterlyReport,
  getReadingsByPeriodReport,
  getSalesCollectionsReport,
  upsertLegalQuarterlyReport,
  type CashMovementsReport,
  type ConsumptionBySectorReport,
  type DelinquencyReport,
  type ExecutiveQuarterlyReport,
  type InventoryValuationReport,
  type LegalQuarterlyBankBalance,
  type LegalQuarterlyConnectionMetric,
  type LegalQuarterlyReport,
  type ReadingsByPeriodReport,
  type SalesCollectionsReport,
} from "@/lib/api";

type ReportHistoryItem = {
  id: string;
  reporte: string;
  periodo: string;
  generadoPor: string;
  fecha: string;
  formato: "CSV" | "JSON" | "XLSX";
  tamanoBytes: number;
};

type ReportsSnapshot = {
  sales: SalesCollectionsReport;
  consumption: ConsumptionBySectorReport;
  inventory: InventoryValuationReport;
  readings: ReadingsByPeriodReport;
  cash: CashMovementsReport;
  delinquency: DelinquencyReport;
  executive: ExecutiveQuarterlyReport;
  legalQuarterly: LegalQuarterlyReport;
};

const HISTORY_STORAGE_KEY = "apr_reportes_generados";
const HISTORY_PAGE_SIZE = 8;

const emptySnapshot: ReportsSnapshot = {
  sales: {
    range: { from: new Date().toISOString(), to: new Date().toISOString() },
    sales: { documents: 0, amount: 0 },
    collections: { payments: 0, amount: 0, byMethod: [] },
    collectionRatePct: 0,
    monthly: [],
  },
  consumption: {
    periodCode: null,
    rows: [],
    totals: { sectors: 0, readings: 0, anomalies: 0, totalConsumptionM3: 0 },
  },
  inventory: {
    totalValue: 0,
    byWarehouse: [],
    rows: [],
  },
  readings: {
    rows: [],
    totals: { periods: 0, readings: 0, anomalies: 0 },
  },
  cash: {
    range: { from: new Date().toISOString(), to: new Date().toISOString() },
    summary: { totalIncome: 0, movements: 0, byMethod: [], bySource: [] },
    movements: [],
  },
  delinquency: {
    minDays: 1,
    summary: {
      contracts: 0,
      overdueAmount: 0,
      buckets: { d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0 },
    },
    contracts: [],
  },
  executive: {
    period: {
      year: new Date().getFullYear(),
      quarter: Math.floor(new Date().getMonth() / 3) + 1,
      from: new Date().toISOString(),
      to: new Date().toISOString(),
    },
    kpis: {
      billedAmount: 0,
      collectedAmount: 0,
      collectionRatePct: 0,
      overdueAmount: 0,
      activeDelinquentContracts: 0,
      inventoryValue: 0,
      avgQuarterConsumptionM3: 0,
      operationsCompletedPct: 0,
      activeContracts: 0,
      activeCustomers: 0,
    },
  },
  legalQuarterly: {
    period: {
      year: new Date().getFullYear(),
      quarter: Math.floor(new Date().getMonth() / 3) + 1,
      monthNumbers: [1, 2, 3],
    },
    organization: {
      companyName: "",
      companyRut: "",
      region: "",
      province: "",
      comuna: "",
    },
    profile: {
      serviceScope: "ap_only",
      preparedByName: "",
      preparedByRole: "",
      submittedAt: null,
      notes: "",
    },
    complementary: {
      subsidies: {
        vigentes: 0,
        pct100: 0,
        pct50: 0,
        other: 0,
      },
      tariffAp: {
        fixedCharge: 0,
        ranges: [],
      },
    },
    categories: {
      incomeAp: [],
      expenseAp: [],
      bankAccountTypes: ["corriente", "ahorro_vista", "fondo_mutuo", "deposito_plazo", "otro"],
    },
    accountingLines: [],
    connectionMetrics: [],
    bankBalances: [],
    consumptionDetail: [],
    completion: {
      completedFields: 0,
      totalFields: 0,
      percent: 0,
    },
    validation: {
      status: "ok",
      blockers: 0,
      warnings: 0,
      items: [],
    },
  },
};

export default function ReportesPage() {
  const { session, profile, hasModule } = useSession();
  const canEditLegalReport = hasModule("facturacion");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState<string>("");
  const [savingLegal, setSavingLegal] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [snapshot, setSnapshot] = useState<ReportsSnapshot>(emptySnapshot);
  const [legalDraft, setLegalDraft] = useState<LegalQuarterlyReport["profile"]>({
    serviceScope: "ap_only",
    preparedByName: "",
    preparedByRole: "",
    submittedAt: null,
    notes: "",
  });
  const [legalComplementary, setLegalComplementary] = useState<LegalQuarterlyReport["complementary"]>({
    subsidies: {
      vigentes: 0,
      pct100: 0,
      pct50: 0,
      other: 0,
    },
    tariffAp: {
      fixedCharge: 0,
      ranges: [],
    },
  });
  const [legalAccounting, setLegalAccounting] = useState<ReportsSnapshot["legalQuarterly"]["accountingLines"]>([]);
  const [legalMetrics, setLegalMetrics] = useState<LegalQuarterlyConnectionMetric[]>([]);
  const [legalBanks, setLegalBanks] = useState<LegalQuarterlyBankBalance[]>([]);
  const [history, setHistory] = useState<ReportHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 89);
    return {
      from: toInputDate(from),
      to: toInputDate(now),
      quarter: `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`,
    };
  });

  const quarterOptions = useMemo(() => buildQuarterOptions(), []);

  useEffect(() => {
    document.title = "Reportes | APR Admin";
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ReportHistoryItem[];
      if (Array.isArray(parsed)) {
        setHistory(parsed.slice(0, 20));
        setHistoryPage(1);
      }
    } catch {
      setHistory([]);
      setHistoryPage(1);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const { year, quarter } = parseQuarter(filters.quarter);
        const hasBodega = hasModule("bodega");
        const [sales, consumption, inventory, readings, cash, delinquency, executive, legalQuarterly] = await Promise.all([
          getSalesCollectionsReport(session, { from: filters.from, to: filters.to }),
          getConsumptionBySectorReport(session),
          hasBodega ? getInventoryValuationReport(session) : Promise.resolve(emptySnapshot.inventory),
          getReadingsByPeriodReport(session, 12),
          getCashMovementsReport(session, { from: filters.from, to: filters.to }),
          getDelinquencyReport(session, 1),
          getExecutiveQuarterlyReport(session, { year, quarter }),
          getLegalQuarterlyReport(session, { year, quarter }),
        ]);

        if (!cancelled) {
          setSnapshot({
            sales,
            consumption,
            inventory,
            readings,
            cash,
            delinquency,
            executive,
            legalQuarterly,
          });
          setLegalDraft(legalQuarterly.profile);
          setLegalComplementary(legalQuarterly.complementary);
          setLegalAccounting(legalQuarterly.accountingLines);
          setLegalMetrics(legalQuarterly.connectionMetrics);
          setLegalBanks(legalQuarterly.bankBalances);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "No se pudieron cargar los reportes");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.quarter, filters.to, reloadNonce, session]);

  async function handleQuickDownload(reportId: string): Promise<void> {
    const periodo = `${filters.from} a ${filters.to}`;
    if (reportId === "ventas") {
      await downloadCsvReport("ventas-recaudacion", "Ventas y Recaudación", periodo, snapshot.sales.monthly);
      return;
    }
    if (reportId === "consumo") {
      await downloadCsvReport("consumo-sector", "Consumo por Sector", snapshot.consumption.periodCode ?? "Último período", snapshot.consumption.rows);
      return;
    }
    if (reportId === "inventario") {
      await downloadCsvReport("inventario", "Estado de Inventario", periodo, snapshot.inventory.rows);
      return;
    }
    if (reportId === "lecturas") {
      await downloadCsvReport("lecturas-periodo", "Lecturas del Período", "Últimos 12 períodos", snapshot.readings.rows);
      return;
    }
    if (reportId === "caja") {
      await downloadCsvReport("caja-movimientos", "Caja y Movimientos", periodo, snapshot.cash.movements);
      return;
    }
    if (reportId === "morosidad") {
      await downloadCsvReport("morosidad", "Morosidad", `Desde ${snapshot.delinquency.minDays} día(s)`, snapshot.delinquency.contracts);
    }
  }

  async function handleExecutiveDownload(format: "CSV" | "JSON"): Promise<void> {
    const quarterLabel = `${snapshot.executive.period.year} Q${snapshot.executive.period.quarter}`;
    if (format === "JSON") {
      await downloadJsonReport(
        "informe-trimestral",
        "Informe Trimestral Ejecutivo",
        quarterLabel,
        snapshot.executive,
      );
      return;
    }

    await downloadCsvReport(
      "informe-trimestral",
      "Informe Trimestral Ejecutivo",
      quarterLabel,
      [
        {
          periodo: quarterLabel,
          totalFacturado: snapshot.executive.kpis.billedAmount,
          totalRecaudado: snapshot.executive.kpis.collectedAmount,
          cobranzaPct: snapshot.executive.kpis.collectionRatePct,
          morosidadMonto: snapshot.executive.kpis.overdueAmount,
          contratosMorosos: snapshot.executive.kpis.activeDelinquentContracts,
          inventarioValorizado: snapshot.executive.kpis.inventoryValue,
          consumoPromedioM3: snapshot.executive.kpis.avgQuarterConsumptionM3,
          operacionesCompletadasPct: snapshot.executive.kpis.operationsCompletedPct,
          clientesActivos: snapshot.executive.kpis.activeCustomers,
          contratosActivos: snapshot.executive.kpis.activeContracts,
        },
      ],
    );
  }

  async function handleLegalSave(): Promise<void> {
    if (!session) return;
    if (!canEditLegalReport) {
      setError("Tu rol solo tiene permisos de lectura para el informe legal trimestral.");
      return;
    }
    setSavingLegal(true);
    setError("");
    try {
      const next = await upsertLegalQuarterlyReport(session, {
        year: snapshot.legalQuarterly.period.year,
        quarter: snapshot.legalQuarterly.period.quarter,
        serviceScope: "ap_only",
        preparedByName: legalDraft.preparedByName ?? "",
        preparedByRole: legalDraft.preparedByRole ?? "",
        submittedAt: legalDraft.submittedAt,
        notes: legalDraft.notes ?? "",
        complementary: legalComplementary,
        accountingLines: legalAccounting,
        connectionMetrics: legalMetrics,
        bankBalances: legalBanks,
      });
      setSnapshot((prev) => ({ ...prev, legalQuarterly: next }));
      setLegalDraft(next.profile);
      setLegalComplementary(next.complementary);
      setLegalAccounting(next.accountingLines);
      setLegalMetrics(next.connectionMetrics);
      setLegalBanks(next.bankBalances);
      appendHistory({
        reporte: "Informe Trimestral Legal AP",
        periodo: `${next.period.year} Q${next.period.quarter}`,
        formato: "JSON",
        tamanoBytes: new Blob([JSON.stringify(next)]).size,
      });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo guardar informe legal trimestral");
    } finally {
      setSavingLegal(false);
    }
  }

  async function handleLegalDownload(format: "CSV" | "JSON" | "XLSX"): Promise<void> {
    const periodLabel = `${snapshot.legalQuarterly.period.year} Q${snapshot.legalQuarterly.period.quarter}`;
    if (format === "XLSX") {
      await downloadLegalQuarterlyXlsx(periodLabel);
      return;
    }
    if (format === "JSON") {
      await downloadJsonReport("informe-trimestral-legal-ap", "Informe Trimestral Legal AP", periodLabel, {
        period: snapshot.legalQuarterly.period,
        organization: snapshot.legalQuarterly.organization,
        profile: legalDraft,
        complementary: legalComplementary,
        accountingLines: legalAccounting,
        connectionMetrics: legalMetrics,
        bankBalances: legalBanks,
        consumptionDetail: snapshot.legalQuarterly.consumptionDetail,
      });
      return;
    }

    const rows = buildLegalQuarterlyCsvRows({
      report: snapshot.legalQuarterly,
      profile: legalDraft,
      complementary: legalComplementary,
      accountingLines: legalAccounting,
      connectionMetrics: legalMetrics,
      bankBalances: legalBanks,
      consumptionDetail: snapshot.legalQuarterly.consumptionDetail,
    });
    await downloadCsvReport("informe-trimestral-legal-ap", "Informe Trimestral Legal AP", periodLabel, rows);
  }

  function updateAccountingAmount(
    side: "income" | "expense",
    categoryCode: string,
    month: number,
    value: number,
  ): void {
    setLegalAccounting((prev) =>
      prev.map((line) =>
        line.side === side && line.categoryCode === categoryCode && line.month === month
          ? { ...line, amount: value }
          : line,
      ),
    );
  }

  function updateMetric(month: number, field: keyof LegalQuarterlyConnectionMetric, value: number | null): void {
    setLegalMetrics((prev) =>
      prev.map((row) => (row.month === month ? { ...row, [field]: value } : row)),
    );
  }

  function updateBankBalance(month: number, accountType: LegalQuarterlyBankBalance["accountType"], value: number): void {
    setLegalBanks((prev) =>
      prev.map((row) => (row.month === month && row.accountType === accountType ? { ...row, balance: value } : row)),
    );
  }

  function updateSubsidyField(
    field: keyof LegalQuarterlyReport["complementary"]["subsidies"],
    value: number,
  ): void {
    setLegalComplementary((prev) => ({
      ...prev,
      subsidies: {
        ...prev.subsidies,
        [field]: value,
      },
    }));
  }

  function updateTariffFixedCharge(value: number): void {
    setLegalComplementary((prev) => ({
      ...prev,
      tariffAp: {
        ...prev.tariffAp,
        fixedCharge: value,
      },
    }));
  }

  function updateTariffRange(
    index: number,
    field: keyof NonNullable<LegalQuarterlyReport["complementary"]["tariffAp"]["ranges"]>[number],
    value: string | number,
  ): void {
    setLegalComplementary((prev) => {
      const currentRanges = [...(prev.tariffAp.ranges ?? [])];
      while (currentRanges.length <= index) {
        currentRanges.push({
          label: `Tramo ${currentRanges.length + 1}`,
          half: 0,
          threeQuarter: 0,
          one: 0,
          two: 0,
        });
      }
      const current = currentRanges[index];
      currentRanges[index] = {
        ...current,
        [field]: value,
      } as (typeof currentRanges)[number];
      return {
        ...prev,
        tariffAp: {
          ...prev.tariffAp,
          ranges: currentRanges,
        },
      };
    });
  }

  async function downloadCsvReport<T extends object>(
    filePrefix: string,
    reportName: string,
    periodLabel: string,
    rows: T[],
  ): Promise<void> {
    setDownloading(filePrefix);
    try {
      const csv = toCsv(rows);
      const filename = `${filePrefix}_${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
      triggerDownload(filename, blob);
      appendHistory({
        reporte: reportName,
        periodo: periodLabel,
        formato: "CSV",
        tamanoBytes: blob.size,
      });
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "No se pudo descargar reporte");
    } finally {
      setDownloading("");
    }
  }

  async function downloadJsonReport(
    filePrefix: string,
    reportName: string,
    periodLabel: string,
    payload: unknown,
  ): Promise<void> {
    setDownloading(`${filePrefix}-json`);
    try {
      const text = JSON.stringify(payload, null, 2);
      const filename = `${filePrefix}_${new Date().toISOString().slice(0, 10)}.json`;
      const blob = new Blob([text], { type: "application/json;charset=utf-8;" });
      triggerDownload(filename, blob);
      appendHistory({
        reporte: reportName,
        periodo: periodLabel,
        formato: "JSON",
        tamanoBytes: blob.size,
      });
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "No se pudo descargar reporte");
    } finally {
      setDownloading("");
    }
  }

  async function downloadLegalQuarterlyXlsx(periodLabel: string): Promise<void> {
    setDownloading("informe-trimestral-legal-ap-xlsx");
    try {
      const XLSX = await import("xlsx");
      const months = snapshot.legalQuarterly.period.monthNumbers;
      const monthNames = months.map((month) => monthLabel(month));

      const financialRows: Array<Array<string | number>> = [
        ["Informe Trimestral Legal AP"],
        ["Periodo", periodLabel],
        ["Empresa", snapshot.legalQuarterly.organization.companyName || "APR"],
        ["RUT", snapshot.legalQuarterly.organization.companyRut || ""],
        ["Region", snapshot.legalQuarterly.organization.region || ""],
        ["Comuna", snapshot.legalQuarterly.organization.comuna || ""],
        ["Responsable", legalDraft.preparedByName || ""],
        ["Cargo", legalDraft.preparedByRole || ""],
        ["Fecha envio", legalDraft.submittedAt || ""],
        [],
        ["Ingresos AP", ...monthNames, "Total trimestre"],
      ];

      for (const row of incomeRows) {
        const values = row.values.map((value) => Number(value.amount || 0));
        financialRows.push([
          row.category.label,
          ...values,
          values.reduce((acc, amount) => acc + amount, 0),
        ]);
      }

      financialRows.push([]);
      financialRows.push(["Egresos AP", ...monthNames, "Total trimestre"]);
      for (const row of expenseRows) {
        const values = row.values.map((value) => Number(value.amount || 0));
        financialRows.push([
          row.category.label,
          ...values,
          values.reduce((acc, amount) => acc + amount, 0),
        ]);
      }

      const complementaryRows: Array<Array<string | number>> = [
        ["Subsidios AP"],
        ["Subsidios vigentes", legalComplementary.subsidies.vigentes],
        ["Subsidios 100%", legalComplementary.subsidies.pct100],
        ["Subsidios 50%", legalComplementary.subsidies.pct50],
        ["Otro subsidio", legalComplementary.subsidies.other],
        [],
        ["Tarifas AP"],
        ["Cargo fijo", legalComplementary.tariffAp.fixedCharge],
        ["Tramo", "1/2", "3/4", "1\"", "2\""],
      ];

      for (const range of legalComplementary.tariffAp.ranges) {
        complementaryRows.push([
          range.label,
          Number(range.half || 0),
          Number(range.threeQuarter || 0),
          Number(range.one || 0),
          Number(range.two || 0),
        ]);
      }

      complementaryRows.push([]);
      complementaryRows.push([
        "Mes",
        "Conexiones",
        "Morosos socios",
        "Morosos usuarios",
        "Deuda socios",
        "Deuda usuarios",
        "M3 producidos",
        "M3 facturados",
      ]);
      for (const row of legalMetrics) {
        complementaryRows.push([
          monthLabel(row.month),
          Number(row.totalConnections ?? 0),
          Number(row.moroseSocios ?? 0),
          Number(row.moroseUsuarios ?? 0),
          Number(row.overdueSocios ?? 0),
          Number(row.overdueUsuarios ?? 0),
          Number(row.producedM3 ?? 0),
          Number(row.billedM3 ?? 0),
        ]);
      }

      complementaryRows.push([]);
      complementaryRows.push(["Mes", "Tipo de cuenta", "Saldo", "Nota"]);
      for (const row of legalBanks) {
        complementaryRows.push([
          monthLabel(row.month),
          bankTypeLabel(row.accountType),
          Number(row.balance || 0),
          row.notes || "",
        ]);
      }

      const consumptionRows: Array<Array<string | number>> = [
        [
          "Tipo cliente",
          "ID cliente",
          "Cliente",
          "ID contrato",
          "N° contrato",
          ...monthNames.map((name) => `${name} m3`),
          ...monthNames.map((name) => `${name} $`),
          "Total m3",
          "Total $",
        ],
      ];

      for (const row of snapshot.legalQuarterly.consumptionDetail) {
        consumptionRows.push([
          row.customerType,
          row.customerId,
          row.customerName,
          row.contractId,
          row.contractNumber,
          ...months.map((month) => row.monthValues.find((item) => item.month === month)?.consumptionM3 ?? 0),
          ...months.map((month) => row.monthValues.find((item) => item.month === month)?.billedAmount ?? 0),
          row.totalConsumptionM3,
          row.totalBilledAmount,
        ]);
      }

      const asRows: Array<Array<string | number>> = [
        ["Bloques AS"],
        ["Estado", "No aplica (APR solo AP)"],
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(financialRows), "Financiero AP");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(complementaryRows), "Complementaria AP");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(consumptionRows), "Consumo AP");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(asRows), "AS No Aplica");

      const arrayBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([arrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = `informe_trimestral_legal_ap_${new Date().toISOString().slice(0, 10)}.xlsx`;
      triggerDownload(filename, blob);
      appendHistory({
        reporte: "Informe Trimestral Legal AP",
        periodo: periodLabel,
        formato: "XLSX",
        tamanoBytes: blob.size,
      });
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : "No se pudo descargar reporte XLSX");
    } finally {
      setDownloading("");
    }
  }

  function appendHistory(item: {
    reporte: string;
    periodo: string;
    formato: "CSV" | "JSON" | "XLSX";
    tamanoBytes: number;
  }): void {
    const next: ReportHistoryItem = {
      id: crypto.randomUUID(),
      reporte: item.reporte,
      periodo: item.periodo,
      generadoPor: profile?.fullName?.trim() || session?.email || "Usuario APR",
      fecha: new Date().toISOString(),
      formato: item.formato,
      tamanoBytes: item.tamanoBytes,
    };
    setHistory((prev) => {
      const merged = [next, ...prev].slice(0, 20);
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
    setHistoryPage(1);
  }

  const totalHistoryPages = useMemo(
    () => Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE)),
    [history.length],
  );

  const safeHistoryPage = Math.min(historyPage, totalHistoryPages);

  const pagedHistory = useMemo(() => {
    const start = (safeHistoryPage - 1) * HISTORY_PAGE_SIZE;
    return history.slice(start, start + HISTORY_PAGE_SIZE);
  }, [history, safeHistoryPage]);

  useEffect(() => {
    if (historyPage > totalHistoryPages) {
      setHistoryPage(totalHistoryPages);
    }
  }, [historyPage, totalHistoryPages]);

  const reportCards = useMemo(
    () => [
      {
        id: "ventas",
        nombre: "Ventas y Recaudación",
        descripcion: `${snapshot.sales.sales.documents.toLocaleString("es-CL")} documentos, cobranza ${snapshot.sales.collectionRatePct.toFixed(1)}%`,
        icon: "ri-bar-chart-2-line",
        color: "text-blue-600",
        bg: "bg-blue-50",
        visible: hasModule("facturacion"),
      },
      {
        id: "consumo",
        nombre: "Consumo por Sector",
        descripcion: `${snapshot.consumption.totals.sectors.toLocaleString("es-CL")} sectores, ${snapshot.consumption.totals.totalConsumptionM3.toLocaleString("es-CL")} m³`,
        icon: "ri-drop-line",
        color: "text-cyan-600",
        bg: "bg-cyan-50",
        visible: hasModule("control_lecturas"),
      },
      {
        id: "inventario",
        nombre: "Estado de Inventario",
        descripcion: `${snapshot.inventory.rows.length.toLocaleString("es-CL")} ítems, valor $${snapshot.inventory.totalValue.toLocaleString("es-CL")}`,
        icon: "ri-store-2-line",
        color: "text-violet-600",
        bg: "bg-violet-50",
        visible: hasModule("bodega"),
      },
      {
        id: "lecturas",
        nombre: "Lecturas del Período",
        descripcion: `${snapshot.readings.totals.readings.toLocaleString("es-CL")} lecturas y ${snapshot.readings.totals.anomalies.toLocaleString("es-CL")} anomalías`,
        icon: "ri-dashboard-line",
        color: "text-emerald-600",
        bg: "bg-emerald-50",
        visible: hasModule("control_lecturas"),
      },
      {
        id: "caja",
        nombre: "Caja y Movimientos",
        descripcion: `${snapshot.cash.summary.movements.toLocaleString("es-CL")} movimientos, ingreso $${snapshot.cash.summary.totalIncome.toLocaleString("es-CL")}`,
        icon: "ri-safe-line",
        color: "text-amber-600",
        bg: "bg-amber-50",
        visible: true,
      },
      {
        id: "morosidad",
        nombre: "Morosidad",
        descripcion: `${snapshot.delinquency.summary.contracts.toLocaleString("es-CL")} contratos, deuda $${snapshot.delinquency.summary.overdueAmount.toLocaleString("es-CL")}`,
        icon: "ri-error-warning-line",
        color: "text-red-600",
        bg: "bg-red-50",
        visible: hasModule("clientes"),
      },
    ].filter((card) => card.visible),
    [snapshot, hasModule],
  );

  const legalMonths = snapshot.legalQuarterly.period.monthNumbers;
  const incomeRows = useMemo(
    () =>
      snapshot.legalQuarterly.categories.incomeAp.map((category) => ({
        category,
        values: legalMonths.map((month) => ({
          month,
          amount:
            legalAccounting.find(
              (line) =>
                line.side === "income" &&
                line.categoryCode === category.code &&
                line.month === month,
            )?.amount ?? 0,
        })),
      })),
    [legalAccounting, legalMonths, snapshot.legalQuarterly.categories.incomeAp],
  );
  const expenseRows = useMemo(
    () =>
      snapshot.legalQuarterly.categories.expenseAp.map((category) => ({
        category,
        values: legalMonths.map((month) => ({
          month,
          amount:
            legalAccounting.find(
              (line) =>
                line.side === "expense" &&
                line.categoryCode === category.code &&
                line.month === month,
            )?.amount ?? 0,
        })),
      })),
    [legalAccounting, legalMonths, snapshot.legalQuarterly.categories.expenseAp],
  );
  const legalCompletion = useMemo(
    () => countLegalCompletion(legalAccounting, legalMetrics, legalBanks, legalComplementary),
    [legalAccounting, legalBanks, legalComplementary, legalMetrics],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Módulo de Reportes</h1>
          <p className="text-sm text-gray-500 mt-0.5">Datos reales de ventas, consumo, inventario, caja y morosidad</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={filters.from}
            onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
          />
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
          />
          <select
            value={filters.quarter}
            onChange={(event) => setFilters((prev) => ({ ...prev, quarter: event.target.value }))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white"
          >
            {quarterOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            onClick={() => setReloadNonce((value) => value + 1)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Actualizar
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Reportes Rápidos</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {reportCards.map((report) => (
            <div key={report.id} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${report.bg} mb-3`}>
                <i className={`${report.icon} text-xl ${report.color}`} />
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-1">{report.nombre}</h3>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">{loading ? "Cargando..." : report.descripcion}</p>
              <button
                onClick={() => void handleQuickDownload(report.id)}
                disabled={loading || downloading === report.id}
                className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
              >
                <i className="ri-download-line text-xs" /> {downloading === report.id ? "Generando..." : "Descargar CSV"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-blue-200" style={{ background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)" }}>
        <div className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600">
                  <i className="ri-file-chart-line text-white text-sm" />
                </div>
                <h2 className="text-lg font-bold text-gray-800">Informe Trimestral Ejecutivo</h2>
              </div>
              <p className="text-sm text-gray-600">Resumen consolidado para revisión gerencial y rendición</p>
            </div>
            <div className="text-sm font-medium text-blue-700">
              {snapshot.executive.period.year} Q{snapshot.executive.period.quarter}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
            {[
              { label: "Total Facturado", value: `$${snapshot.executive.kpis.billedAmount.toLocaleString("es-CL")}` },
              { label: "Total Recaudado", value: `$${snapshot.executive.kpis.collectedAmount.toLocaleString("es-CL")}` },
              { label: "Tasa Cobranza", value: `${snapshot.executive.kpis.collectionRatePct.toFixed(1)}%` },
              { label: "Clientes Activos", value: snapshot.executive.kpis.activeCustomers.toLocaleString("es-CL") },
              { label: "Operaciones Completadas", value: `${snapshot.executive.kpis.operationsCompletedPct.toFixed(1)}%` },
            ].map((item) => (
              <div key={item.label} className="bg-white/80 rounded-xl p-4 border border-white">
                <div className="text-xs text-gray-500">{item.label}</div>
                <div className="text-lg font-bold text-gray-800 mt-1">{loading ? "..." : item.value}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={() => void handleExecutiveDownload("CSV")}
              disabled={loading || downloading === "informe-trimestral"}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-excel-2-line" /></div>
              Exportar CSV
            </button>
            <button
              onClick={() => void handleExecutiveDownload("JSON")}
              disabled={loading || downloading === "informe-trimestral-json"}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-60"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-code-s-slash-line" /></div>
              Exportar JSON
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-emerald-200 overflow-hidden">
        <div className="p-5 border-b border-emerald-100 bg-emerald-50/70">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Informe Trimestral Legal AP</h2>
              <p className="text-sm text-gray-600 mt-1">
                Captura oficial para comité (solo agua potable) con guardado real en base de datos.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {snapshot.legalQuarterly.organization.companyName || "APR"} |{" "}
                {snapshot.legalQuarterly.organization.comuna || "Comuna sin configurar"} |{" "}
                {snapshot.legalQuarterly.organization.region || "Región sin configurar"}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Período</div>
              <div className="text-sm font-semibold text-emerald-700">
                {snapshot.legalQuarterly.period.year} Q{snapshot.legalQuarterly.period.quarter}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Avance: {legalCompletion.completedFields}/{legalCompletion.totalFields} ({legalCompletion.percent.toFixed(1)}%)
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
            <label className="text-xs text-gray-600">
              Nombre responsable
              <input
                value={legalDraft.preparedByName ?? ""}
                onChange={(event) => setLegalDraft((prev) => ({ ...prev, preparedByName: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-xs text-gray-600">
              Cargo
              <input
                value={legalDraft.preparedByRole ?? ""}
                onChange={(event) => setLegalDraft((prev) => ({ ...prev, preparedByRole: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-xs text-gray-600">
              Fecha envío
              <input
                type="date"
                value={toInputDateSafe(legalDraft.submittedAt)}
                onChange={(event) =>
                  setLegalDraft((prev) => ({
                    ...prev,
                    submittedAt: event.target.value ? `${event.target.value}T00:00:00.000Z` : null,
                  }))
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-xs text-gray-600">
              Observaciones
              <input
                value={legalDraft.notes ?? ""}
                onChange={(event) => setLegalDraft((prev) => ({ ...prev, notes: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              />
            </label>
          </div>
        </div>

          <div className="p-5 space-y-5">
          {snapshot.legalQuarterly.validation.items.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-amber-800">
                  Validaciones del informe legal
                </p>
                <p className="text-xs text-amber-700">
                  Bloqueantes: {snapshot.legalQuarterly.validation.blockers} | Advertencias: {snapshot.legalQuarterly.validation.warnings}
                </p>
              </div>
              <ul className="mt-2 space-y-1">
                {snapshot.legalQuarterly.validation.items.map((item) => (
                  <li key={item.code} className="text-xs text-amber-900">
                    <span className="font-semibold">{item.severity === "blocker" ? "Bloqueante" : "Advertencia"}:</span>{" "}
                    {item.message} {item.detail}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              Validaciones normativas: sin observaciones para este periodo.
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Ingresos AP</h3>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Rubro</th>
                    {legalMonths.map((month) => (
                      <th key={`in-month-${month}`} className="px-3 py-2 text-right text-xs text-gray-500 uppercase">
                        {monthLabel(month)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incomeRows.map((row) => (
                    <tr key={`in-${row.category.code}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{row.category.label}</td>
                      {row.values.map((value) => (
                        <td key={`in-${row.category.code}-${value.month}`} className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={value.amount}
                            onChange={(event) =>
                              updateAccountingAmount(
                                "income",
                                row.category.code,
                                value.month,
                                Number(event.target.value || 0),
                              )
                            }
                            className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Egresos AP</h3>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Rubro</th>
                    {legalMonths.map((month) => (
                      <th key={`ex-month-${month}`} className="px-3 py-2 text-right text-xs text-gray-500 uppercase">
                        {monthLabel(month)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expenseRows.map((row) => (
                    <tr key={`ex-${row.category.code}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{row.category.label}</td>
                      {row.values.map((value) => (
                        <td key={`ex-${row.category.code}-${value.month}`} className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            value={value.amount}
                            onChange={(event) =>
                              updateAccountingAmount(
                                "expense",
                                row.category.code,
                                value.month,
                                Number(event.target.value || 0),
                              )
                            }
                            className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Conexiones, morosidad y producción</h3>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Mes</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Conexiones</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Morosos socios</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Morosos usuarios</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Deuda socios</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Deuda usuarios</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">M3 producidos</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">M3 facturados</th>
                  </tr>
                </thead>
                <tbody>
                  {legalMetrics.map((row) => (
                    <tr key={`metric-${row.month}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{monthLabel(row.month)}</td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.totalConnections ?? 0} onChange={(event) => updateMetric(row.month, "totalConnections", Number(event.target.value || 0))} className="w-full min-w-20 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.moroseSocios ?? 0} onChange={(event) => updateMetric(row.month, "moroseSocios", Number(event.target.value || 0))} className="w-full min-w-20 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.moroseUsuarios ?? 0} onChange={(event) => updateMetric(row.month, "moroseUsuarios", Number(event.target.value || 0))} className="w-full min-w-20 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.overdueSocios ?? 0} onChange={(event) => updateMetric(row.month, "overdueSocios", Number(event.target.value || 0))} className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.overdueUsuarios ?? 0} onChange={(event) => updateMetric(row.month, "overdueUsuarios", Number(event.target.value || 0))} className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.producedM3 ?? 0} onChange={(event) => updateMetric(row.month, "producedM3", Number(event.target.value || 0))} className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                      <td className="px-3 py-2"><input type="number" min={0} value={row.billedM3 ?? 0} onChange={(event) => updateMetric(row.month, "billedM3", Number(event.target.value || 0))} className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Saldos bancarios</h3>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Tipo cuenta</th>
                    {legalMonths.map((month) => (
                      <th key={`bank-month-${month}`} className="px-3 py-2 text-right text-xs text-gray-500 uppercase">
                        {monthLabel(month)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshot.legalQuarterly.categories.bankAccountTypes.map((accountType) => (
                    <tr key={`bank-${accountType}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-700">{bankTypeLabel(accountType)}</td>
                      {legalMonths.map((month) => {
                        const balance =
                          legalBanks.find((row) => row.month === month && row.accountType === accountType)?.balance ?? 0;
                        return (
                          <td key={`bank-${accountType}-${month}`} className="px-3 py-2">
                            <input
                              type="number"
                              min={0}
                              value={balance}
                              onChange={(event) =>
                                updateBankBalance(month, accountType, Number(event.target.value || 0))
                              }
                              className="w-full min-w-24 text-right rounded-md border border-gray-200 px-2 py-1.5"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-gray-100 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Subsidios AP</h3>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-600">
                  Vigentes
                  <input
                    type="number"
                    min={0}
                    value={legalComplementary.subsidies.vigentes}
                    onChange={(event) => updateSubsidyField("vigentes", Number(event.target.value || 0))}
                    className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-right"
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Subsidio 100%
                  <input
                    type="number"
                    min={0}
                    value={legalComplementary.subsidies.pct100}
                    onChange={(event) => updateSubsidyField("pct100", Number(event.target.value || 0))}
                    className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-right"
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Subsidio 50%
                  <input
                    type="number"
                    min={0}
                    value={legalComplementary.subsidies.pct50}
                    onChange={(event) => updateSubsidyField("pct50", Number(event.target.value || 0))}
                    className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-right"
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Otro subsidio
                  <input
                    type="number"
                    min={0}
                    value={legalComplementary.subsidies.other}
                    onChange={(event) => updateSubsidyField("other", Number(event.target.value || 0))}
                    className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-right"
                  />
                </label>
              </div>
            </div>

            <div className="border border-gray-100 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Tarifas vigentes AP</h3>
              <label className="text-xs text-gray-600 block mb-3">
                Cargo fijo
                <input
                  type="number"
                  min={0}
                  value={legalComplementary.tariffAp.fixedCharge}
                  onChange={(event) => updateTariffFixedCharge(Number(event.target.value || 0))}
                  className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-right"
                />
              </label>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left py-1">Tramo</th>
                      <th className="text-right py-1">1/2</th>
                      <th className="text-right py-1">3/4</th>
                      <th className="text-right py-1">1\"</th>
                      <th className="text-right py-1">2\"</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 5 }).map((_, index) => {
                      const row = legalComplementary.tariffAp.ranges[index] ?? {
                        label: `Tramo ${index + 1}`,
                        half: 0,
                        threeQuarter: 0,
                        one: 0,
                        two: 0,
                      };
                      return (
                        <tr key={`tariff-${index}`}>
                          <td className="py-1 pr-2">
                            <input
                              value={row.label}
                              onChange={(event) => updateTariffRange(index, "label", event.target.value)}
                              className="w-full rounded-md border border-gray-200 px-2 py-1"
                            />
                          </td>
                          <td className="py-1 px-1"><input type="number" min={0} value={row.half ?? 0} onChange={(event) => updateTariffRange(index, "half", Number(event.target.value || 0))} className="w-20 rounded-md border border-gray-200 px-2 py-1 text-right" /></td>
                          <td className="py-1 px-1"><input type="number" min={0} value={row.threeQuarter ?? 0} onChange={(event) => updateTariffRange(index, "threeQuarter", Number(event.target.value || 0))} className="w-20 rounded-md border border-gray-200 px-2 py-1 text-right" /></td>
                          <td className="py-1 px-1"><input type="number" min={0} value={row.one ?? 0} onChange={(event) => updateTariffRange(index, "one", Number(event.target.value || 0))} className="w-20 rounded-md border border-gray-200 px-2 py-1 text-right" /></td>
                          <td className="py-1 px-1"><input type="number" min={0} value={row.two ?? 0} onChange={(event) => updateTariffRange(index, "two", Number(event.target.value || 0))} className="w-20 rounded-md border border-gray-200 px-2 py-1 text-right" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => void handleLegalDownload("XLSX")}
              disabled={loading || downloading === "informe-trimestral-legal-ap-xlsx"}
              className="px-4 py-2 text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-60"
            >
              Exportar XLSX legal
            </button>
            <button
              onClick={() => void handleLegalDownload("CSV")}
              disabled={loading || downloading === "informe-trimestral-legal-ap"}
              className="px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-60"
            >
              Exportar CSV legal
            </button>
            <button
              onClick={() => void handleLegalDownload("JSON")}
              disabled={loading || downloading === "informe-trimestral-legal-ap-json"}
              className="px-4 py-2 text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-60"
            >
              Exportar JSON legal
            </button>
            {canEditLegalReport ? (
              <button
                onClick={() => void handleLegalSave()}
                disabled={savingLegal || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
              >
                {savingLegal ? "Guardando..." : "Guardar informe legal"}
              </button>
            ) : (
              <span className="px-2 py-1 text-xs text-gray-500">Solo lectura para tu rol</span>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Consumo m3 AP por cliente (Hoja 3)</h3>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-500 uppercase">Cliente</th>
                    <th className="px-3 py-2 text-left text-gray-500 uppercase">Contrato</th>
                    {legalMonths.map((month) => (
                      <th key={`cons-month-${month}`} className="px-3 py-2 text-right text-gray-500 uppercase">
                        {monthLabel(month)} m3
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right text-gray-500 uppercase">Total m3</th>
                    <th className="px-3 py-2 text-right text-gray-500 uppercase">Total $</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.legalQuarterly.consumptionDetail.length === 0 ? (
                    <tr>
                      <td colSpan={legalMonths.length + 4} className="px-3 py-4 text-center text-gray-500">
                        Sin datos de consumo para el periodo seleccionado.
                      </td>
                    </tr>
                  ) : (
                    snapshot.legalQuarterly.consumptionDetail.slice(0, 40).map((row) => (
                      <tr key={row.contractId} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">
                          {row.customerName}
                          <div className="text-[11px] text-gray-500">{row.customerType}</div>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{row.contractNumber}</td>
                        {legalMonths.map((month) => (
                          <td key={`${row.contractId}-${month}`} className="px-3 py-2 text-right text-gray-700">
                            {(row.monthValues.find((item) => item.month === month)?.consumptionM3 ?? 0).toLocaleString("es-CL")}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right font-medium text-gray-800">{row.totalConsumptionM3.toLocaleString("es-CL")}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-800">${row.totalBilledAmount.toLocaleString("es-CL")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {snapshot.legalQuarterly.consumptionDetail.length > 40 ? (
              <p className="text-xs text-gray-500 mt-2">
                Mostrando 40 de {snapshot.legalQuarterly.consumptionDetail.length} clientes. Exporta CSV/JSON para el detalle completo.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Reportes Generados Recientemente</h2>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Tipo de Reporte", "Período", "Generado por", "Fecha", "Formato", "Tamaño"].map((header) => (
                  <th key={header} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                    Aún no se han generado reportes en esta sesión.
                  </td>
                </tr>
              ) : (
                pagedHistory.map((row, index) => (
                  <tr key={row.id} className={`border-b border-gray-50 ${index % 2 === 1 ? "bg-gray-50/30" : ""}`}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.reporte}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.periodo}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.generadoPor}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(row.fecha)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.formato}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(row.tamanoBytes)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {history.length > 0 ? (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/60">
              <div className="text-xs text-gray-500">
                Mostrando {(safeHistoryPage - 1) * HISTORY_PAGE_SIZE + 1}-
                {Math.min(safeHistoryPage * HISTORY_PAGE_SIZE, history.length)} de {history.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                  disabled={safeHistoryPage <= 1}
                  className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="text-xs text-gray-600">
                  Página {safeHistoryPage} de {totalHistoryPages}
                </span>
                <button
                  onClick={() => setHistoryPage((prev) => Math.min(totalHistoryPages, prev + 1))}
                  disabled={safeHistoryPage >= totalHistoryPages}
                  className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function toInputDate(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildQuarterOptions(): Array<{ value: string; label: string }> {
  const now = new Date();
  const currentYear = now.getFullYear();
  const options: Array<{ value: string; label: string }> = [];
  for (let year = currentYear; year >= currentYear - 1; year -= 1) {
    for (let quarter = 4; quarter >= 1; quarter -= 1) {
      options.push({
        value: `${year}-Q${quarter}`,
        label: `${year} Q${quarter}`,
      });
    }
  }
  return options;
}

function parseQuarter(value: string): { year: number; quarter: number } {
  const [yearRaw, quarterRaw] = value.split("-Q");
  const year = Number(yearRaw);
  const quarter = Number(quarterRaw);
  if (!Number.isFinite(year) || !Number.isFinite(quarter) || quarter < 1 || quarter > 4) {
    const now = new Date();
    return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 };
  }
  return { year, quarter };
}

function toInputDateSafe(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return toInputDate(date);
}

function monthLabel(month: number): string {
  const safe = Math.min(12, Math.max(1, Math.trunc(month)));
  const date = new Date(Date.UTC(2026, safe - 1, 1));
  return new Intl.DateTimeFormat("es-CL", { month: "long" }).format(date);
}

function bankTypeLabel(
  value: "corriente" | "ahorro_vista" | "fondo_mutuo" | "deposito_plazo" | "otro",
): string {
  const labels: Record<typeof value, string> = {
    corriente: "Cuenta corriente",
    ahorro_vista: "Cuenta ahorro/vista",
    fondo_mutuo: "Fondo mutuo",
    deposito_plazo: "Depósito a plazo",
    otro: "Otro",
  };
  return labels[value];
}

function countLegalCompletion(
  accountingLines: Array<{ amount: number }>,
  metrics: LegalQuarterlyConnectionMetric[],
  bankBalances: LegalQuarterlyBankBalance[],
  complementary?: LegalQuarterlyReport["complementary"],
): { completedFields: number; totalFields: number; percent: number } {
  const accounting = accountingLines.filter((item) => Number(item.amount) > 0).length;
  const metricFields = metrics.reduce((acc, row) => {
    let count = acc;
    if ((row.totalConnections ?? 0) > 0) count += 1;
    if ((row.moroseSocios ?? 0) > 0) count += 1;
    if ((row.moroseUsuarios ?? 0) > 0) count += 1;
    if ((row.overdueSocios ?? 0) > 0 || (row.overdueUsuarios ?? 0) > 0) count += 1;
    if ((row.producedM3 ?? 0) > 0) count += 1;
    if ((row.billedM3 ?? 0) > 0) count += 1;
    return count;
  }, 0);
  const banks = bankBalances.filter((item) => Number(item.balance) > 0).length;
  const subsidyCompleted =
    complementary == null
      ? 0
      : [
          complementary.subsidies.vigentes,
          complementary.subsidies.pct100,
          complementary.subsidies.pct50,
          complementary.subsidies.other,
        ].filter((value) => Number(value) > 0).length;
  const tariffRangeCount = complementary?.tariffAp.ranges?.length ?? 0;
  const tariffCompleted =
    complementary == null
      ? 0
      : (Number(complementary.tariffAp.fixedCharge) > 0 ? 1 : 0) +
        (complementary.tariffAp.ranges ?? []).reduce((acc, range) => {
          let count = acc;
          if (range.label.trim() !== "") count += 1;
          if (Number(range.half) > 0) count += 1;
          if (Number(range.threeQuarter) > 0) count += 1;
          if (Number(range.one) > 0) count += 1;
          if (Number(range.two) > 0) count += 1;
          return count;
        }, 0);
  const completedFields = accounting + metricFields + banks + subsidyCompleted + tariffCompleted;
  const totalFields = accountingLines.length + metrics.length * 6 + bankBalances.length + 4 + 1 + tariffRangeCount * 5;
  return {
    completedFields,
    totalFields,
    percent: totalFields > 0 ? (completedFields * 100) / totalFields : 0,
  };
}

function buildLegalQuarterlyCsvRows(input: {
  report: LegalQuarterlyReport;
  profile: LegalQuarterlyReport["profile"];
  complementary: LegalQuarterlyReport["complementary"];
  accountingLines: LegalQuarterlyReport["accountingLines"];
  connectionMetrics: LegalQuarterlyConnectionMetric[];
  bankBalances: LegalQuarterlyBankBalance[];
  consumptionDetail: LegalQuarterlyReport["consumptionDetail"];
}): Array<Record<string, string | number>> {
  const rows: Array<Record<string, string | number>> = [];
  const periodLabel = `${input.report.period.year} Q${input.report.period.quarter}`;
  rows.push({
    seccion: "Encabezado",
    periodo: periodLabel,
    empresa: input.report.organization.companyName,
    rut: input.report.organization.companyRut,
    region: input.report.organization.region,
    provincia: input.report.organization.province,
    comuna: input.report.organization.comuna,
    responsable: input.profile.preparedByName ?? "",
    cargo: input.profile.preparedByRole ?? "",
    fechaEnvio: input.profile.submittedAt ?? "",
  });

  for (const line of input.accountingLines) {
    rows.push({
      seccion: line.side === "income" ? "Ingresos AP" : "Egresos AP",
      categoriaCodigo: line.categoryCode,
      categoria: line.categoryLabel,
      mes: monthLabel(line.month),
      monto: line.amount,
    });
  }

  rows.push({
    seccion: "Subsidios AP",
    item: "Subsidios vigentes",
    valor: input.complementary.subsidies.vigentes,
  });
  rows.push({
    seccion: "Subsidios AP",
    item: "Subsidios 100%",
    valor: input.complementary.subsidies.pct100,
  });
  rows.push({
    seccion: "Subsidios AP",
    item: "Subsidios 50%",
    valor: input.complementary.subsidies.pct50,
  });
  rows.push({
    seccion: "Subsidios AP",
    item: "Otro subsidio",
    valor: input.complementary.subsidies.other,
  });

  rows.push({
    seccion: "Tarifas AP",
    item: "Cargo fijo",
    valor: input.complementary.tariffAp.fixedCharge,
  });

  for (const range of input.complementary.tariffAp.ranges) {
    rows.push({
      seccion: "Tarifas AP",
      item: range.label,
      tarifaMedia: range.half,
      tarifaTresCuartos: range.threeQuarter,
      tarifaUnaPulgada: range.one,
      tarifaDosPulgadas: range.two,
    });
  }

  for (const metric of input.connectionMetrics) {
    rows.push({
      seccion: "Conexiones y morosidad",
      mes: monthLabel(metric.month),
      conexionesTotales: metric.totalConnections ?? 0,
      morososSocios: metric.moroseSocios ?? 0,
      morososUsuarios: metric.moroseUsuarios ?? 0,
      deudaSocios: metric.overdueSocios ?? 0,
      deudaUsuarios: metric.overdueUsuarios ?? 0,
      producidoM3: metric.producedM3 ?? 0,
      facturadoM3: metric.billedM3 ?? 0,
    });
  }

  for (const balance of input.bankBalances) {
    rows.push({
      seccion: "Saldos bancarios",
      mes: monthLabel(balance.month),
      tipoCuenta: bankTypeLabel(balance.accountType),
      saldo: balance.balance,
      nota: balance.notes ?? "",
    });
  }

  for (const row of input.consumptionDetail) {
    for (const monthData of row.monthValues) {
      rows.push({
        seccion: "Consumo AP detalle",
        tipoCliente: row.customerType,
        clienteId: row.customerId,
        cliente: row.customerName,
        contratoId: row.contractId,
        contrato: row.contractNumber,
        mes: monthLabel(monthData.month),
        consumoM3: monthData.consumptionM3,
        montoFacturado: monthData.billedAmount,
      });
    }
    rows.push({
      seccion: "Consumo AP detalle",
      tipoCliente: row.customerType,
      clienteId: row.customerId,
      cliente: row.customerName,
      contratoId: row.contractId,
      contrato: row.contractNumber,
      mes: "Total trimestre",
      consumoM3: row.totalConsumptionM3,
      montoFacturado: row.totalBilledAmount,
    });
  }

  rows.push({
    seccion: "Bloques AS",
    item: "Estado",
    valor: "No aplica (APR solo AP)",
  });

  return rows;
}

function toCsv<T extends object>(rows: T[]): string {
  if (rows.length === 0) return "sin_datos\n";
  const normalizedRows = rows as Array<Record<string, unknown>>;
  const columns = Object.keys(normalizedRows[0]);
  const head = columns.map((column) => toSpanishHeader(column)).join(";");
  const body = normalizedRows.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(";"));
  return [head, ...body].join("\n");
}

function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  const normalized = String(value);
  if (/[;"\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, "\"\"")}"`;
  }
  return normalized;
}

function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toSpanishHeader(column: string): string {
  const labels: Record<string, string> = {
    month: "Mes",
    billedAmount: "Monto facturado",
    billedDocuments: "Documentos facturados",
    collectedAmount: "Monto recaudado",
    collectedPayments: "Pagos recaudados",
    sector: "Sector",
    contracts: "Contratos",
    readings: "Lecturas",
    anomalies: "Anomalías",
    totalConsumptionM3: "Consumo total (m³)",
    avgConsumptionM3: "Consumo promedio (m³)",
    warehouseName: "Bodega",
    sku: "SKU",
    itemName: "Producto",
    quantityOnHand: "Stock disponible",
    unitCost: "Costo unitario",
    totalValue: "Valor total",
    periodCode: "Período",
    status: "Estado",
    expectedReadings: "Lecturas esperadas",
    completionPct: "Cumplimiento (%)",
    openedAt: "Fecha apertura",
    closedAt: "Fecha cierre",
    paidAt: "Fecha de pago",
    contractNumber: "N.° de contrato",
    customer: "Cliente",
    method: "Medio de pago",
    source: "Origen",
    amount: "Monto",
    allocations: "Asignaciones",
    receiptNumber: "N.° de comprobante",
    reference: "Referencia",
    contractId: "ID contrato",
    address: "Dirección",
    documents: "Documentos",
    overdueAmount: "Deuda vencida",
    maxDaysOverdue: "Máximo días de mora",
    periodo: "Período",
    totalFacturado: "Total facturado",
    totalRecaudado: "Total recaudado",
    cobranzaPct: "Cobranza (%)",
    morosidadMonto: "Monto morosidad",
    contratosMorosos: "Contratos morosos",
    inventarioValorizado: "Inventario valorizado",
    consumoPromedioM3: "Consumo promedio (m³)",
    operacionesCompletadasPct: "Operaciones completadas (%)",
    clientesActivos: "Clientes activos",
    contratosActivos: "Contratos activos",
    seccion: "Sección",
    empresa: "Empresa",
    rut: "RUT",
    region: "Región",
    provincia: "Provincia",
    comuna: "Comuna",
    responsable: "Responsable",
    cargo: "Cargo",
    fechaEnvio: "Fecha envío",
    categoriaCodigo: "Código categoría",
    categoria: "Categoría",
    mes: "Mes",
    monto: "Monto",
    conexionesTotales: "Conexiones totales",
    morososSocios: "Morosos socios",
    morososUsuarios: "Morosos usuarios",
    deudaSocios: "Deuda socios",
    deudaUsuarios: "Deuda usuarios",
    producidoM3: "Producido (m³)",
    facturadoM3: "Facturado (m³)",
    tipoCuenta: "Tipo cuenta",
    saldo: "Saldo",
    nota: "Nota",
    item: "Ítem",
    valor: "Valor",
    tarifaMedia: "Tarifa 1/2",
    tarifaTresCuartos: "Tarifa 3/4",
    tarifaUnaPulgada: "Tarifa 1\"",
    tarifaDosPulgadas: "Tarifa 2\"",
    tipoCliente: "Tipo cliente",
    clienteId: "ID cliente",
    cliente: "Cliente",
    contratoId: "ID contrato",
    contrato: "Contrato",
    consumoM3: "Consumo (m3)",
    montoFacturado: "Monto facturado",
  };
  return labels[column] ?? column;
}
